import React, { useCallback, useMemo, useState } from 'react';

import { CheckIcon, ChevronDownIcon, ExternalLinkIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface PopoverComboboxOption {
  value: string;
  label: string;
  id?: string | number;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  externalLink?: string;
  externalLinkTooltip?: string;
}

export interface PopoverComboboxProps {
  options: PopoverComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  triggerPlaceholder?: string;
  /** i18n key (not pre-translated text) for the search input placeholder. Defaults to `common.search_dots` (or `common.search_or_type_to_add` when `onAddNewOption` is set). */
  searchPlaceholderKey?: string;
  onAddNewOption?: (value: string) => void;
  /**
   * Label for the "Add new" item. Can be a plain string, or a function that receives
   * the current search term so the label can include it (e.g. `Add "billing"`).
   */
  createNewText?: string | ((searchTerm: string) => string);
  className?: string;
  disabled?: boolean;
  maxWidth?: string;
  error?: boolean;
  trigger?: React.ReactNode;
  /** Forwarded to the default trigger button so a `<label htmlFor>` can be associated with it. */
  id?: string;
  /** When false, clicking a selected option will not deselect it. Defaults to true. */
  allowDeselect?: boolean;
  /** Extra classes forwarded to PopoverComboboxContent (e.g. z-index overrides inside MUI modals). */
  contentClassName?: string;
  /** Accessibility attributes forwarded to the default trigger button. */
  'aria-required'?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  /** Native attributes forwarded to the search input (e.g. `PREVENT_AUTOFILL_PROPS` for comboboxes a password manager mistakes for a login field). */
  inputProps?: React.ComponentProps<typeof CommandInput>;
}

export const PopoverComboboxTrigger = ({
  children,
  className,
  maxWidth,
  error,
  disabled,
  open,
  ref,
  ...props
}: React.ComponentProps<'button'> & { maxWidth?: string; error?: boolean; open?: boolean }) => {
  return (
    <button
      ref={ref}
      type="button"
      role="combobox"
      aria-expanded={open}
      aria-controls="popover-combobox-list"
      className={cn(
        'flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        maxWidth,
        error && 'border-warning',
        disabled && 'cursor-default text-muted-foreground',
        className
      )}
      disabled={disabled}
      {...props}
    >
      <span className="truncate overflow-hidden">{children}</span>
      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
};

export const PopoverComboboxContent = ({
  children,
  className,
  commandProps,
  ref,
  ...props
}: React.ComponentProps<typeof PopoverContent> & {
  commandProps?: React.ComponentProps<typeof Command>;
}) => {
  return (
    <PopoverContent ref={ref} className={cn('p-0', className)} align="start" {...props}>
      <Command shouldFilter={false} {...commandProps}>
        {children}
      </Command>
    </PopoverContent>
  );
};

export const PopoverComboboxInput = ({
  className,
  placeholder = 'common.search_dots',
  ref,
  ...props
}: React.ComponentProps<typeof CommandInput>) => {
  const { t } = useTranslation();
  return (
    <CommandInput
      ref={ref}
      className={cn('h-9', className)}
      placeholder={t(placeholder)}
      {...props}
    />
  );
};

export const PopoverComboboxList = ({
  children,
  className,
  id = 'popover-combobox-list',
  ref,
  ...props
}: React.ComponentProps<typeof CommandList>) => {
  return (
    <CommandList
      ref={ref}
      id={id}
      className={cn('max-h-[calc(var(--radix-popover-content-available-height)-3rem)]', className)}
      {...props}
    >
      {children}
    </CommandList>
  );
};

export const PopoverComboboxGroup = CommandGroup;

export const PopoverComboboxItem = ({
  children,
  selected,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandItem> & { selected?: boolean }) => {
  return (
    <CommandItem ref={ref} className={className} {...props}>
      {children}
      {selected && <CheckIcon className="ml-auto h-4 w-4 text-primary" />}
    </CommandItem>
  );
};

export const PopoverComboboxEmpty = ({
  children,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandEmpty>) => {
  return (
    <CommandEmpty
      ref={ref}
      className={cn('px-2 py-3 text-center text-sm text-muted-foreground', className)}
      {...props}
    >
      {children}
    </CommandEmpty>
  );
};

export const PopoverCombobox = ({
  options,
  value,
  onChange,
  searchValue: externalSearchValue,
  onSearchChange: externalOnSearchChange,
  triggerPlaceholder,
  searchPlaceholderKey,
  onAddNewOption,
  createNewText,
  className = '',
  disabled = false,
  maxWidth = '',
  error = false,
  trigger,
  allowDeselect = false,
  contentClassName,
  id,
  'aria-required': ariaRequired,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
  inputProps,
}: PopoverComboboxProps) => {
  // UI state
  const [open, setOpen] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState('');

  const { t } = useTranslation();

  const triggerPlaceholderLabel = triggerPlaceholder || t('common.select_an_option');
  // Use external or internal search state
  const isExternalSearch =
    externalSearchValue !== undefined && externalOnSearchChange !== undefined;
  const searchTerm = isExternalSearch ? externalSearchValue : internalSearchTerm;

  const createNewTextLabel =
    typeof createNewText === 'function'
      ? createNewText(searchTerm)
      : createNewText || t('common.add_new_plain');

  const searchInputPlaceholder =
    searchPlaceholderKey ||
    (onAddNewOption ? 'common.search_or_type_to_add' : 'common.search_dots');

  const handleSearchChange = useCallback(
    (newValue: string) => {
      if (isExternalSearch) {
        externalOnSearchChange!(newValue);
      } else {
        setInternalSearchTerm(newValue);
      }
    },
    [isExternalSearch, externalOnSearchChange]
  );

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Handle adding the searched value as a new option
  const handleAddSearchedValue = useCallback(() => {
    if (searchTerm && onAddNewOption) {
      onAddNewOption(searchTerm);
      setOpen(false);
    }
  }, [searchTerm, onAddNewOption]);

  // "Add new" only makes sense when the user has typed something that doesn't already
  // match any existing option. When any option matches (partial or exact) the user can
  // pick that one instead.
  const showAddNewOption = useMemo(() => {
    if (!onAddNewOption) {
      return false;
    }
    if (!searchTerm.trim()) {
      return false;
    }
    return filteredOptions.length === 0;
  }, [onAddNewOption, searchTerm, filteredOptions]);

  // Get the selected option for the trigger button
  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  const selectedLabel = selectedOption?.label || value || triggerPlaceholderLabel;
  const listId = id ? `${id}-list` : 'popover-combobox-list';

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <PopoverComboboxTrigger
            id={id}
            open={open}
            className={className}
            maxWidth={maxWidth}
            error={error}
            disabled={disabled}
            aria-controls={listId}
            aria-required={ariaRequired}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedby}
          >
            <span className="flex min-w-0 items-center gap-2">
              {selectedOption?.icon}
              <span className="truncate">{selectedLabel}</span>
              {selectedOption?.endIcon && (
                <span className="inline-flex shrink-0 items-center [&_svg]:pointer-events-auto">
                  {selectedOption.endIcon}
                </span>
              )}
            </span>
          </PopoverComboboxTrigger>
        )}
      </PopoverTrigger>
      <PopoverComboboxContent className={contentClassName}>
        <PopoverComboboxInput
          {...inputProps}
          value={searchTerm}
          onValueChange={handleSearchChange}
          placeholder={searchInputPlaceholder}
        />
        <PopoverComboboxList id={listId}>
          {filteredOptions.length > 0 ? (
            <PopoverComboboxGroup>
              {filteredOptions.map((option) => {
                const isSelected = value === option.value;
                const hasExternalLink = !!option.externalLink;
                return (
                  <PopoverComboboxItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      // Toggle behavior: unselect if already selected (only if allowDeselect is true)
                      if (isSelected && allowDeselect) {
                        onChange('');
                      } else if (!isSelected) {
                        onChange(option.value);
                      }
                      setOpen(false);
                    }}
                    selected={!hasExternalLink && isSelected}
                    className="group justify-between"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      {option.icon}
                      <span className="truncate">{option.label}</span>
                      {option.endIcon && (
                        <span className="inline-flex shrink-0 items-center [&_svg]:pointer-events-auto">
                          {option.endIcon}
                        </span>
                      )}
                    </div>
                    {hasExternalLink && (
                      <div className="flex shrink-0 items-center">
                        <InfoTooltip
                          content={option.externalLinkTooltip || ''}
                          side="right"
                          delayDuration={200}
                          size="auto"
                          className="max-w-[200px] text-center break-all"
                        >
                          <Link
                            to={option.externalLink}
                            target="_blank"
                            onClick={(e) => e.stopPropagation()}
                            className={cn(
                              'transition-opacity',
                              isSelected
                                ? 'hidden group-hover:block'
                                : 'opacity-0 group-hover:opacity-100'
                            )}
                          >
                            <ExternalLinkIcon className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                          </Link>
                        </InfoTooltip>
                        {isSelected && (
                          <CheckIcon className="h-4 w-4 text-primary group-hover:hidden" />
                        )}
                      </div>
                    )}
                  </PopoverComboboxItem>
                );
              })}
            </PopoverComboboxGroup>
          ) : (
            // When an add-new action is available it's the clear next step, so skip the
            // "No results found" line and let the Add button speak for itself.
            searchTerm &&
            !showAddNewOption && (
              <PopoverComboboxEmpty>{t('common.no_results')}</PopoverComboboxEmpty>
            )
          )}
          {showAddNewOption && (
            <PopoverComboboxGroup forceMount={true}>
              <PopoverComboboxItem
                onSelect={() => {
                  handleAddSearchedValue();
                  setOpen(false);
                }}
                forceMount={true}
                value={`add-new-${searchTerm}`}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                {createNewTextLabel}
              </PopoverComboboxItem>
            </PopoverComboboxGroup>
          )}
        </PopoverComboboxList>
      </PopoverComboboxContent>
    </Popover>
  );
};
